import {
  Box,
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { UploadMultiFile } from 'src/components/upload'
import axios from 'axios'
import { uploadFile } from '../../api/bed/upload'
import { createGeneSet } from '../../api/gene/geneSet'
import { useFiles, useBedFiles } from '../../api/file/list'
import KitsAndPanelsTable from './KitsAndPanelsTable'
import validGenes from '../../constants/genes.json'
import { useSnackbar } from 'notistack5'
import { chemistrySupported, DEFAULT_CHEMISTRY } from 'src/utils/bedChemistry'
import BedChemistryField from './BedChemistryField'

let cancelTokenSource = null

const UploadKitsPanels = ({ isKits }) => {
  const [fileQueue, setFileQueue] = useState([])
  const [state, setState] = useState('IDLE')
  const [uploadProgress, setUploadProgress] = useState(0)
  const [reference, setReference] = useState('GRCh38')
  const [chemistry, setChemistry] = useState(DEFAULT_CHEMISTRY)
  const [isReferenceDialogOpen, setIsReferenceDialogOpen] = useState(false)
  const [invalidGenes, setInvalidGenes] = useState([])
  const [isGeneValidationDialogOpen, setIsGeneValidationDialogOpen] = useState(false)
  const [isNotesDialogOpen, setIsNotesDialogOpen] = useState(false)
  const [notes, setNotes] = useState('')
  const filesApi = useFiles()
  const { query: bedFilesQuery, refresh: refreshBedFiles } = useBedFiles()
  // The chemistry choice is offered (and sent) only once the kit list carries one.
  const bedChemistryOn = chemistrySupported(bedFilesQuery.data)
  const [genePanelsUpdated, setGenePanelsUpdated] = useState(0)
  const titleToShow = isKits ? 'Capture Kits' : 'Gene Panels'
  const { enqueueSnackbar } = useSnackbar()

  const handleDropMultiFile = async (filesToUpload) => {
    setFileQueue(filesToUpload)
    if (isKits) {
      setIsReferenceDialogOpen(true)
    } else {
      validateGenePanel(filesToUpload[0])
    }
  }

  const validateGenePanel = async (file) => {
    const content = await readFileContent(file)
    const genes = content
      .split(/[\n,]/)
      .map((gene) => gene.trim())
      .filter((gene) => gene)
    const invalidGenes = genes.filter((gene) => !validGenes.includes(gene))

    if (invalidGenes.length > 0) {
      setInvalidGenes(invalidGenes)
      setIsGeneValidationDialogOpen(true)
    } else {
      setIsNotesDialogOpen(true)
    }
  }

  const readFileContent = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (event) => resolve(event.target.result)
      reader.onerror = (error) => reject(error)
      reader.readAsText(file)
    })
  }

  const handleUploadFiles = async () => {
    setState('UPLOADING')
  }

  const handleRemoveAll = () => {
    setFileQueue([])
  }

  const handleRemove = (file) => {
    setFileQueue(fileQueue.filter((f) => f !== file))
  }

  const handleCancel = () => {
    setFileQueue([])
    cancelTokenSource.cancel('Cancelled by user')
  }

  const handleReferenceChange = (event) => {
    setReference(event.target.value)
  }

  const handleReferenceDialogClose = () => {
    setIsReferenceDialogOpen(false)
    setState('READY_TO_UPLOAD')
  }

  const handleGeneValidationDialogClose = () => {
    setIsGeneValidationDialogOpen(false)
    setFileQueue([])
  }

  const handleNotesDialogClose = () => {
    setIsNotesDialogOpen(false)
    setState('READY_TO_UPLOAD')
  }

  const handleNotesChange = (event) => {
    setNotes(event.target.value)
  }

  useEffect(() => {
    const uploadFilesStateMachine = async () => {
      if (state === 'UPLOADING') {
        if (fileQueue.length > 0) {
          setUploadProgress(0)
          cancelTokenSource = axios.CancelToken.source()

          try {
            if (isKits) {
              await uploadFile(
                fileQueue[0],
                (value) => setUploadProgress(value),
                cancelTokenSource.token,
                fileQueue[0].name,
                reference,
                bedChemistryOn ? chemistry : undefined,
              )
              refreshBedFiles()
            } else {
              const content = await readFileContent(fileQueue[0])
              const genes = content
                .split(/[\n,]/)
                .map((gene) => gene.trim())
                .filter((gene) => gene)
              await createGeneSet(fileQueue[0].name.replace(/\.[^.]+$/, ''), genes, notes)
              setGenePanelsUpdated((prev) => prev + 1)
              setNotes('')
              enqueueSnackbar('Gene panel uploaded successfully', { variant: 'success' })
            }

            setFileQueue(fileQueue.slice(1))
            await filesApi.refresh()
          } catch (error) {
            console.error('Error uploading file:', error)
            enqueueSnackbar(error.message, { variant: 'error' })
            setState('IDLE')
          }
        } else {
          setState('IDLE')
        }
      }
    }

    uploadFilesStateMachine()
  }, [state])

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader title={`Upload ${titleToShow}`} />
      <CardContent>
        {state === 'UPLOADING' && fileQueue.length > 0 && (
          <Box py={2} px={1}>
            <Box display="flex" flexDirection="row">
              <Typography variant="subtitle1" color="textSecondary">
                Uploading&nbsp;
              </Typography>
              <Typography variant="subtitle1" color="textPrimary">
                {fileQueue[0].name}
              </Typography>
              <Typography variant="subtitle1" color="textSecondary" pl={1}>
                ({uploadProgress}%)&nbsp;...
              </Typography>
            </Box>
            <Box p={1} />
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}
        <UploadMultiFile
          maxSize={1024 * 1024 * 1024}
          files={fileQueue}
          onDrop={handleDropMultiFile}
          onRemove={handleRemove}
          onRemoveAll={handleRemoveAll}
          onUploadFiles={handleUploadFiles}
          locked={state === 'UPLOADING'}
          onCancel={handleCancel}
          accept={isKits ? '.bed' : '.txt,.csv'}
        />
      </CardContent>

      <KitsAndPanelsTable isKits={isKits} genePanelsUpdated={genePanelsUpdated} />

      <Dialog open={isReferenceDialogOpen} onClose={handleReferenceDialogClose}>
        <DialogTitle>{bedChemistryOn ? 'Reference and library chemistry' : 'Select Reference'}</DialogTitle>
        <DialogContent>
          <RadioGroup aria-label="reference" name="reference" value={reference} onChange={handleReferenceChange}>
            <FormControlLabel value="GRCh37" control={<Radio />} label="GRCh37" />
            <FormControlLabel value="GRCh38" control={<Radio />} label="GRCh38" />
          </RadioGroup>
          {bedChemistryOn && <BedChemistryField value={chemistry} onChange={setChemistry} sx={{ mt: 2 }} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReferenceDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleReferenceDialogClose} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isGeneValidationDialogOpen} onClose={handleGeneValidationDialogClose}>
        <DialogTitle>Invalid Genes Detected</DialogTitle>
        <DialogContent>
          <Typography>The following genes are not valid:</Typography>
          <Typography color="error">
            {invalidGenes.slice(0, 10).join(', ')}
            {invalidGenes.length > 10 ? ` (and ${invalidGenes.length - 10} more)` : ''}
          </Typography>
          <Typography>Please remove these genes and try uploading again.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleGeneValidationDialogClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isNotesDialogOpen} onClose={handleNotesDialogClose}>
        <DialogTitle>Add Notes</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="notes"
            label="Notes"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={notes}
            onChange={handleNotesChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNotesDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleNotesDialogClose} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}

export default UploadKitsPanels
